import { useEffect, useRef, useState } from "react";
import EnvelopeTable from "@/components/tables/EnvelopeTable";
import TableHead from "@/components/tables/TableHead";
import TransferOwnershipButton from "@/components/buttons/TransferOwnershipButton";
import TransferOwnershipModal from "@/components/modals/TransferOwnershipModal";
import {
    fetchEnvos,
    transferBulkEnvelopeOwnership,
} from "@/services/envelope.service";
import { StatusOptions } from "@/utils/constants/constants";
import { useFeaturePermission } from "@/utils/hooks/useFeaturePermission";
import { useDataQueries, normalizeSearchItem } from "@/utils/hooks/useDataQueries";
import {
    buildTransferOwnershipPayload,
    useOwnershipSelection,
} from "@/utils/hooks/useOwnershipSelection";
import { useCompactListLayout } from "@/utils/hooks/useCompactListLayout";
import { useWindowInfiniteScroll } from "@/utils/hooks/useWindowInfiniteScroll";
import {
    getListCacheBootstrap,
    invalidateListCacheByPage,
    invalidateOwnershipTransferListCaches,
    writeListCache,
} from "@/utils/listPageCache";
import { LABELS } from "@/utils/constants/appConstants/commonConstants";
import {
    BULK_TRANSFER_OWNERSHIP_QUEUED,
    BULK_TRANSFER_OWNERSHIP_SUCCESS,
} from "@/utils/constants/messages";
import { FEATURE_PERMISSIONS } from "@/utils/constants/featurePermissions";
import { errorToasterMessage, successToasterMessage } from "@/utils/functions/toaster";

const isEmptyFilter = (value: Record<string, unknown>) =>
    Object.keys(value).length === 0;

const EnvelopeList = () => {
    const [
        pageNo,
        setPageNo,
        dataLength,
        setDataLength,
        sorting,
        setSorting,
        filter,
        setFilter,
        searchItem,
        setSearchItem,
        resetDataQueries,
    ] = useDataQueries();
    const isCompactLayout = useCompactListLayout();
    const selection = useOwnershipSelection();
    const [showBulkTransferModal, setShowBulkTransferModal] = useState(false);
    const [bulkTransferLoading, setBulkTransferLoading] = useState(false);
    const showTransferActions = useFeaturePermission(FEATURE_PERMISSIONS.AdminPortalTransferEnvelopeOwnership);

    const listBootstrap = getListCacheBootstrap("envelopes", {
        dataLength,
        sorting,
        searchItem,
        filter,
    });

    const [appliedFilter, setAppliedFilter] = useState(filter);

    const [isLoading, setIsLoading] = useState(
        !listBootstrap.hasCache
    );

    const [envelopes, setEnvelopes] = useState<any[]>(
        listBootstrap.items
    );

    const [envelopesLeft, setEnvelopesLeft] = useState(true);

    const [envelopesHistory, setEnvelopesHistory] = useState<any[]>(
        listBootstrap.items
    );

    const [allFetched, setAllFetched] = useState(false);

    const [loadData, setLoadData] = useState(
        !listBootstrap.hasCache
    );

    const [totalCount, setTotalCount] = useState(
        listBootstrap.totalCount
    );
    const loadMoreLockRef = useRef(false);

    /*
     * Fetch first page.
     *
     * Normal navigation can use cache.
     * Reset / Apply Filter bypasses cache.
     */
    useEffect(() => {
        const { cacheKey, cached } = getListCacheBootstrap("envelopes", {
            dataLength,
            sorting,
            searchItem,
            filter: appliedFilter,
        });
    
        setPageNo(1);
        setEnvelopesLeft(true);
        setAllFetched(false);
    
        if (cached && isEmptyFilter(appliedFilter)) {
            const cachedItems = cached.items as any[];
            const hasMore = cachedItems.length < cached.totalCount;
            setEnvelopes(cachedItems);
            setEnvelopesHistory(cachedItems);
            setTotalCount(cached.totalCount);
            setEnvelopesLeft(hasMore);
            setAllFetched(!hasMore);
            setLoadData(false);
            setIsLoading(false);
            loadMoreLockRef.current = false;
            return;
        }
    
        setEnvelopes([]);
        setEnvelopesHistory([]);
        setTotalCount(0);
        setLoadData(true);
        setIsLoading(true);
    
        fetchEnvos(
            1,
            dataLength,
            sorting,
            appliedFilter,
            searchItem,
            (response: any) => {
                const nextEnvelopes =
                    response.data?.envelopes ?? [];
    
                const total =
                    response.data?.total_count ?? 0;
    
                writeListCache(
                    cacheKey,
                    nextEnvelopes,
                    total
                );
    
                setLoadData(false);
                setIsLoading(false);
                setTotalCount(total);
                setEnvelopes(nextEnvelopes);
                setEnvelopesHistory(nextEnvelopes);
                loadMoreLockRef.current = false;

                const hasMore =
                    nextEnvelopes.length > 0 &&
                    (total > 0
                        ? nextEnvelopes.length < total
                        : nextEnvelopes.length >= dataLength);
                setEnvelopesLeft(hasMore);
                setAllFetched(!hasMore);
            },
            (error: any) => {
                console.error("Failed to fetch envelopes:", error);
                setLoadData(false);
                setIsLoading(false);
                loadMoreLockRef.current = false;
            }
        );
    }, [
        dataLength,
        sorting,
        searchItem,
        appliedFilter,
    ]);

    useWindowInfiniteScroll({
        enabled: !allFetched && !isLoading && envelopesLeft,
        onLoadMore: () => {
            if (loadMoreLockRef.current) {
                return;
            }
            loadMoreLockRef.current = true;

            const nextPage = Number(pageNo) + 1;
            setPageNo(nextPage);
            setIsLoading(true);

            fetchEnvos(
                nextPage,
                dataLength,
                sorting,
                appliedFilter,
                searchItem,
                (response: any) => {
                    const nextEnvelopes =
                        response.data?.envelopes ?? [];
                    const reportedTotal = response.data?.total_count;

                    setIsLoading(false);
                    loadMoreLockRef.current = false;

                    setEnvelopes((prev) => {
                        const merged = [...prev, ...nextEnvelopes];
                        const total = reportedTotal ?? merged.length;
                        setTotalCount(total);
                        const hasMore =
                            nextEnvelopes.length > 0 &&
                            (reportedTotal != null
                                ? merged.length < reportedTotal
                                : nextEnvelopes.length >= dataLength);
                        setEnvelopesLeft(hasMore);
                        setAllFetched(!hasMore);
                        return merged;
                    });

                    setEnvelopesHistory((prev) => [
                        ...prev,
                        ...nextEnvelopes,
                    ]);
                },
                (error: any) => {
                    console.error(
                        "Failed to fetch more envelopes:",
                        error
                    );
                    setIsLoading(false);
                    loadMoreLockRef.current = false;
                }
            );
        },
    });

    const search = (searchText: string) => {
        setSearchItem(normalizeSearchItem(searchText));
    };

      const reset = () => {
        const defaultFilter = {};

        invalidateListCacheByPage("envelopes");

        setPageNo(1);
        setEnvelopesLeft(true);
        setAllFetched(false);

        setEnvelopes([]);
        setEnvelopesHistory([]);
        setTotalCount(0);

        setLoadData(true);
        setIsLoading(true);

        setFilter(defaultFilter);
        setAppliedFilter(defaultFilter);
        setSearchItem("");
        selection.clear();

        resetDataQueries?.();
    };

    const handleApplyFilter = (filterOverride?: any) => {
        const nextFilter = filterOverride ?? filter;

        invalidateListCacheByPage("envelopes");

        setPageNo(1);
        setEnvelopesLeft(true);
        setAllFetched(false);

        setEnvelopes([]);
        setEnvelopesHistory([]);
        setTotalCount(0);

        setLoadData(true);
        setIsLoading(true);

        setAppliedFilter(nextFilter);
    };

    const envelopeIds = envelopes.map((envelope) => envelope.id).filter(Boolean);

    const reloadEnvelopes = () => {
        invalidateOwnershipTransferListCaches("envelope");
        setPageNo(1);
        setEnvelopesLeft(true);
        setAllFetched(false);
        setEnvelopes([]);
        setEnvelopesHistory([]);
        setTotalCount(0);
        setLoadData(true);
        setIsLoading(true);
        setAppliedFilter((prev: Record<string, any>) => ({ ...prev }));
    };

    const handleBulkTransferOwnership = async (selectedUser: any) => {
        if (!selectedUser || selection.selectedIds.length === 0) return;

        setBulkTransferLoading(true);
        try {
            const payload = buildTransferOwnershipPayload(selectedUser);
            const result = await transferBulkEnvelopeOwnership({
                envelope_ids: selection.selectedIds,
                ...payload,
            });
            selection.clear();
            setShowBulkTransferModal(false);
            successToasterMessage(
                result?.is_async
                    ? BULK_TRANSFER_OWNERSHIP_QUEUED
                    : BULK_TRANSFER_OWNERSHIP_SUCCESS,
            );
            reloadEnvelopes();
        } catch (error) {
            console.log("Error bulk transferring envelope ownership:", error);
            errorToasterMessage(error);
        } finally {
            setBulkTransferLoading(false);
        }
    };

    return (
        <div className="gs-list-page-card">
            <div className="gs-list-page-card-header">
                <TableHead
                    id="envelope-table"
                    handleReset={reset}
                    pageNo={pageNo as number}
                    dataLength={dataLength as number}
                    setFilter={setFilter}
                    filter={filter}
                    setDataLength={
                        setDataLength as (
                            dataLength: number
                        ) => void
                    }
                    reset={reset}
                    search={search}
                    title="Envelopes"
                    options={StatusOptions}
                    handleApplyFilter={handleApplyFilter}
                    currentEnvo={envelopes.length}
                    totalEnvo={totalCount}
                    searchItem={searchItem as string}
                    selectedCount={showTransferActions ? selection.selectedIds.length : 0}
                    showMultiselectSelectAll={showTransferActions && isCompactLayout && envelopeIds.length > 0}
                    selectAllChecked={
                        selection.areAllSelected(envelopeIds)
                            ? true
                            : selection.areSomeSelected(envelopeIds)
                              ? "indeterminate"
                              : false
                    }
                    onSelectAllChange={() => selection.toggleAll(envelopeIds)}
                    multiselectAction={
                        showTransferActions ? (
                            <TransferOwnershipButton
                                id="envelope-table-bulk-transfer-btn"
                                iconId="envelope-table-bulk-transfer-icon"
                                className="gs-transfer-ownership-btn--toolbar"
                                onClick={() => setShowBulkTransferModal(true)}
                            />
                        ) : null
                    }
                />
            </div>

            <div>
                <EnvelopeTable
                    id="envelope-table"
                    reset={reset}
                    title="Envelopes"
                    setEnvelopes={setEnvelopes}
                    search={search}
                    isLoading={isLoading}
                    dataLength={dataLength as number}
                    sorting={sorting}
                    setSorting={
                        setSorting as (
                            sorting: any
                        ) => void
                    }
                    searchItem={searchItem as string}
                    allFetched={allFetched}
                    envelopes={envelopes}
                    dataLoad={loadData}
                    resetDataQueries={reloadEnvelopes}
                    setPageNo={setPageNo}
                    selection={selection}
                    showTransferActions={showTransferActions}
                />
            </div>
            {showBulkTransferModal ? (
                <TransferOwnershipModal
                    show={showBulkTransferModal}
                    onHide={() => setShowBulkTransferModal(false)}
                    onTransferOwnership={handleBulkTransferOwnership}
                    loading={bulkTransferLoading}
                    title={LABELS.TRANSFER_ENVELOPE_OWNERSHIP}
                    description={LABELS.BULK_TRANSFER_ENVELOPE_OWNERSHIP_DESCRIPTION}
                />
            ) : null}
        </div>
    );
};

export default EnvelopeList;
